import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { apiFetch } from '../../utils/api'
import { calculatePayroll, centsToEur, type PayrollInput, type PayrollResult } from './payroll-engine'
import { generateBulletinPDF } from './pdf/generate-bulletin'
import { buildBulletinData } from './pdf/build-bulletin-data'
import { useAuth } from '../../hooks/useAuth'
import type { TaxClass } from '../../config/rts'
import styles from './RunPayrollPage.module.css'

interface Employee {
  id: string; firstName: string; lastName: string; taxClass: string
  rtsPersonalRate: number | null; grossMonthlySalary: number | null
  weeklyHours: number | null; matriculeCcss: string | null
  department: string | null; position: string | null
}

interface GridRow {
  employee: Employee
  baseSalary: number
  overtimeHours: string
  thirteenthMonth: string
  mealVouchers: string
  absenceDays: string
  carBenefit: string
  result: PayrollResult | null
  selected: boolean
}

interface ReadinessItem {
  type: 'blocker' | 'warning'
  code: string
  message: string
  employeeId?: string
  employeeName?: string
  details?: Record<string, unknown>
}

type Step = 'grid' | 'results' | 'done'
const MN = ['Janvier','Fevrier','Mars','Avril','Mai','Juin','Juillet','Aout','Septembre','Octobre','Novembre','Decembre']

export function RunPayrollPage() {
  const { t } = useTranslation()
  const { user } = useAuth()
  const [step, setStep] = useState<Step>('grid')
  const [year, setYear] = useState(new Date().getFullYear())
  const [month, setMonth] = useState(new Date().getMonth() + 1)
  const [rows, setRows] = useState<GridRow[]>([])
  const [loading, setLoading] = useState(true)
  const [generating, setGenerating] = useState(false)
  const [validating, setValidating] = useState(false)
  const [progress, setProgress] = useState({ current: 0, total: 0 })
  const [pdfs, setPdfs] = useState<Array<{ name: string; blob: Blob }>>([])
  const [savedIds, setSavedIds] = useState<string[]>([])
  const [readiness, setReadiness] = useState<{ items: ReadinessItem[]; ready: boolean } | null>(null)
  const [periodStatus, setPeriodStatus] = useState<string>('open')

  useEffect(() => { loadGrid() }, [year, month])

  const loadGrid = async () => {
    setLoading(true)
    setReadiness(null)
    setSavedIds([])
    setPdfs([])

    const empRes = await apiFetch<{ employees: Employee[] }>('/employees?active=true')
    const employees = empRes.data?.employees ?? []

    // Pre-fill from previous month
    const pm = month === 1 ? 12 : month - 1
    const py = month === 1 ? year - 1 : year
    const prevRes = await apiFetch<{ payslips: Array<Record<string, unknown>> }>(`/payslips?year=${py}&month=${pm}`)
    const prevSlips = prevRes.data?.payslips ?? []

    // Check if drafts already exist for this period
    const currentRes = await apiFetch<{ payslips: Array<Record<string, unknown>>; periodStatus: string }>(`/payslips?year=${year}&month=${month}`)
    const currentSlips = currentRes.data?.payslips ?? []
    setPeriodStatus(currentRes.data?.periodStatus ?? 'open')

    setRows(employees.map(emp => {
      const existing = currentSlips.find(p => p['employee_id'] === emp.id)
      const prev = prevSlips.find(p => p['employee_id'] === emp.id)
      let pd: Record<string, number> = {}
      if (existing) {
        try { pd = JSON.parse((existing['details_json'] as string) ?? '{}') } catch { /* */ }
      } else if (prev) {
        try { pd = JSON.parse((prev['details_json'] as string) ?? '{}') } catch { /* */ }
      }
      return {
        employee: emp, baseSalary: emp.grossMonthlySalary ?? 0,
        overtimeHours: String(pd['overtimeHours'] ?? 0),
        thirteenthMonth: '0',
        mealVouchers: String(pd['mealVouchersCount'] ?? 20),
        absenceDays: '0', carBenefit: '0',
        result: null, selected: true,
      }
    }))
    setStep('grid'); setLoading(false)
  }

  const upd = (i: number, f: string, v: string) => setRows(p => p.map((r, j) => j === i ? { ...r, [f]: v } : r))
  const tog = (i: number) => setRows(p => p.map((r, j) => j === i ? { ...r, selected: !r.selected } : r))

  const calculateAll = () => {
    setRows(p => p.map(r => {
      if (r.baseSalary <= 0) return { ...r, result: null }
      const input: PayrollInput = {
        grossBaseCents: r.baseSalary,
        taxClass: (r.employee.taxClass ?? '1') as TaxClass,
        rtsPersonalRate: r.employee.rtsPersonalRate ?? undefined,
        overtimeHours: parseFloat(r.overtimeHours || '0'),
        thirteenthMonthCents: Math.round(parseFloat(r.thirteenthMonth || '0') * 100),
        mealVouchersCount: parseInt(r.mealVouchers || '0', 10),
        mealVoucherValueCents: 1080,
        carBenefitCents: Math.round(parseFloat(r.carBenefit || '0') * 100),
        housingBenefitCents: 0,
      }
      return { ...r, result: calculatePayroll(input) }
    }))
    setStep('results')
  }

  // Save all selected payslips as DRAFT + generate PDFs
  const generate = async () => {
    setGenerating(true)
    const sel = rows.filter(r => r.selected && r.result)
    setProgress({ current: 0, total: sel.length })
    const out: Array<{ name: string; blob: Blob }> = []
    const ids: string[] = []

    // Load previous payslips of the year for cumuls
    const prevRes = await apiFetch<{ payslips: Array<Record<string, unknown>> }>(`/payslips?year=${year}`)
    const allPrevPayslips = prevRes.data?.payslips ?? []

    for (let i = 0; i < sel.length; i++) {
      const r = sel[i]!; const res = r.result!
      setProgress({ current: i + 1, total: sel.length })

      // Save as DRAFT (upsert)
      const saveRes = await apiFetch<{ id: string }>('/payslips', { method: 'POST', body: JSON.stringify({
        employeeId: r.employee.id, periodYear: year, periodMonth: month,
        grossSalaryCents: res.grossTotalCents, ssEmployeeCents: res.ssEmployeeTotalCents,
        ssEmployerCents: res.ssEmployerTotalCents, taxRtsCents: res.rtsCents,
        netSalaryCents: res.netCents, totalCostCents: res.totalCostCents,
        aVerserCents: res.aVerserCents, detailsJson: JSON.stringify(res),
      })})

      if (saveRes.data?.id) ids.push(saveRes.data.id)

      // Build cumuls from previous months for this employee
      const empPrev = allPrevPayslips
        .filter(p => p['employee_id'] === r.employee.id && (p['period_month'] as number) < month)
        .map(p => {
          const d = JSON.parse((p['details_json'] as string) ?? '{}') as Record<string, number>
          return {
            grossCents: (p['gross_salary_cents'] as number) ?? 0,
            cotisationsCents: (p['ss_employee_cents'] as number) ?? 0,
            abattementCents: d['totalAbatementsCents'] ?? 0,
            imposableCents: d['taxableIncomeCents'] ?? 0,
            impotsCents: (p['tax_rts_cents'] as number) ?? 0,
            netCents: (p['net_salary_cents'] as number) ?? 0,
            aVerserCents: d['aVerserCents'] ?? 0,
          }
        })

      try {
        const bd = buildBulletinData({
          result: res,
          employee: r.employee,
          company: { name: user?.company.name ?? '' },
          year, month,
          overtimeHours: parseFloat(r.overtimeHours || '0'),
          mealVouchersCount: parseInt(r.mealVouchers || '0', 10),
          previousPayslips: empPrev,
        })
        const blob = await generateBulletinPDF(bd)
        out.push({ name: `bulletin_${r.employee.lastName}_${r.employee.firstName}_${year}_${String(month).padStart(2, '0')}.pdf`, blob })
      } catch { /* */ }
    }

    setSavedIds(ids)
    setPdfs(out)

    // Fetch readiness check
    const rc = await apiFetch<{ ready: boolean; items: ReadinessItem[] }>(`/payslips/readiness?year=${year}&month=${month}`)
    setReadiness(rc.data ?? null)

    setStep('done'); setGenerating(false)
  }

  // Validate all draft payslips of this run
  const validateAll = async () => {
    setValidating(true)
    let ok = 0
    for (const id of savedIds) {
      const res = await apiFetch(`/payslips/${id}/validate`, { method: 'PUT' })
      if (!res.error) ok++
    }
    setValidating(false)
    // Reload to reflect new status
    if (ok > 0) {
      setSavedIds([])
      loadGrid()
    }
  }

  const dl = (p: { name: string; blob: Blob }) => { const u = URL.createObjectURL(p.blob); const a = document.createElement('a'); a.href = u; a.download = p.name; document.body.appendChild(a); a.click(); document.body.removeChild(a); URL.revokeObjectURL(u) }

  const eligible = rows.filter(r => r.baseSalary > 0)
  const selRes = rows.filter(r => r.selected && r.result)
  const totG = selRes.reduce((s, r) => s + (r.result?.grossTotalCents ?? 0), 0)
  const totV = selRes.reduce((s, r) => s + (r.result?.aVerserCents ?? 0), 0)
  const totC = selRes.reduce((s, r) => s + (r.result?.totalCostCents ?? 0), 0)
  const isClosed = periodStatus === 'closed'

  if (loading) return <div className={styles.page}><p>{t('common.loading')}</p></div>

  return (
    <div className={styles.page}>
      <div className={styles.pageHeader}>
        <h1>{t('run_payroll.title')}</h1>
        <div className={styles.periodSelect}>
          <select className={styles.select} value={month} onChange={e => setMonth(Number(e.target.value))}>
            {MN.map((n, i) => <option key={i} value={i + 1}>{n}</option>)}
          </select>
          <select className={styles.select} value={year} onChange={e => setYear(Number(e.target.value))}>
            {[2025, 2026, 2027].map(y => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>
      </div>

      {isClosed && (
        <div className={styles.closedBanner}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="11" width="18" height="11" rx="2" /><path d="M7 11V7a5 5 0 0110 0v4" /></svg>
          <span>{t('run_payroll.period_closed')}</span>
        </div>
      )}

      {/* === GRID: saisie mensuelle === */}
      {step === 'grid' && !isClosed && (
        <>
          <p className={styles.employeeCount}>
            {eligible.length} / {rows.length} {t('run_payroll.eligible_employees')}
            {eligible.length === 0 && rows.length > 0 && <span className={styles.warning}> — {t('run_payroll.no_salary')}</span>}
          </p>

          <div className={styles.gridWrapper}>
            <div className={styles.gridHeader}>
              <span className={styles.gridColName}>{t('employees.title')}</span>
              <span className={styles.gridCol}>{t('run_payroll.base')}</span>
              <span className={styles.gridCol}>{t('run_payroll.overtime_h')}</span>
              <span className={styles.gridCol}>{t('run_payroll.thirteenth')}</span>
              <span className={styles.gridCol}>{t('run_payroll.meal_count')}</span>
              <span className={styles.gridCol}>{t('run_payroll.absence_days')}</span>
            </div>
            {rows.map((r, i) => (
              <div key={r.employee.id} className={`${styles.gridRow} ${r.baseSalary <= 0 ? styles.gridRowDisabled : ''}`}>
                <span className={styles.gridColName}>
                  <strong>{r.employee.lastName} {r.employee.firstName}</strong>
                  {r.employee.position && <small>{r.employee.position}</small>}
                </span>
                <span className={styles.gridCol}>{r.baseSalary > 0 ? centsToEur(r.baseSalary) : <em>{t('run_payroll.no_salary')}</em>}</span>
                <span className={styles.gridCol}><input type="number" value={r.overtimeHours} onChange={e => upd(i, 'overtimeHours', e.target.value)} min="0" step="0.5" disabled={r.baseSalary <= 0} /></span>
                <span className={styles.gridCol}><input type="number" value={r.thirteenthMonth} onChange={e => upd(i, 'thirteenthMonth', e.target.value)} min="0" disabled={r.baseSalary <= 0} /></span>
                <span className={styles.gridCol}><input type="number" value={r.mealVouchers} onChange={e => upd(i, 'mealVouchers', e.target.value)} min="0" disabled={r.baseSalary <= 0} /></span>
                <span className={styles.gridCol}><input type="number" value={r.absenceDays} onChange={e => upd(i, 'absenceDays', e.target.value)} min="0" step="0.5" disabled={r.baseSalary <= 0} /></span>
              </div>
            ))}
          </div>

          <div className={styles.actionBar}>
            <button className={styles.primaryBtn} onClick={calculateAll} disabled={eligible.length === 0}>
              {t('run_payroll.calculate')} ({eligible.length})
            </button>
          </div>
        </>
      )}

      {/* === RESULTS === */}
      {step === 'results' && (
        <>
          <div className={styles.totalsGrid}>
            <div className={styles.totalCard}><span className={styles.totalValue}>{centsToEur(totG)}</span><span className={styles.totalLabel}>{t('run_payroll.total_gross')}</span></div>
            <div className={styles.totalCard}><span className={styles.totalValue}>{centsToEur(totV)}</span><span className={styles.totalLabel}>{t('run_payroll.total_a_verser')}</span></div>
            <div className={styles.totalCard}><span className={styles.totalValue}>{centsToEur(totC)}</span><span className={styles.totalLabel}>{t('run_payroll.total_cost')}</span></div>
          </div>
          <div className={styles.tableWrapper}>
            {rows.filter(r => r.result).map((r, i) => (
              <div key={r.employee.id} className={`${styles.lineRow} ${!r.selected ? styles.lineDeselected : ''}`}>
                <label className={styles.lineCheck}><input type="checkbox" checked={r.selected} onChange={() => tog(i)} /></label>
                <span className={styles.lineName}>{r.employee.lastName} {r.employee.firstName}</span>
                <span className={styles.lineValue}>{centsToEur(r.result!.grossTotalCents)}</span>
                <span className={styles.lineValue}>{centsToEur(r.result!.ssEmployeeTotalCents)}</span>
                <span className={styles.lineValue}>{centsToEur(r.result!.rtsCents)}</span>
                <span className={styles.lineValueBold}>{centsToEur(r.result!.aVerserCents)}</span>
              </div>
            ))}
          </div>
          <div className={styles.actionBar}>
            <button className={styles.secondaryBtn} onClick={() => setStep('grid')}>{t('common.back')}</button>
            <button className={styles.primaryBtn} onClick={generate} disabled={generating || selRes.length === 0}>
              {generating ? `${progress.current}/${progress.total}` : `${t('run_payroll.save_drafts')} (${selRes.length})`}
            </button>
          </div>
        </>
      )}

      {/* === DONE === */}
      {step === 'done' && (
        <div className={styles.donePanel}>
          <div className={styles.doneIcon}><svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M20 6L9 17l-5-5" /></svg></div>
          <h2>{t('run_payroll.done_title')}</h2>
          <p>{t('run_payroll.done_subtitle_draft', { count: savedIds.length, period: `${MN[month - 1]} ${year}` })}</p>

          {/* Readiness check */}
          {readiness && readiness.items.length > 0 && (
            <div className={styles.readinessPanel}>
              <h3 className={styles.readinessTitle}>{t('readiness.title')}</h3>
              {readiness.items.map((item, i) => (
                <div key={i} className={`${styles.readinessItem} ${item.type === 'blocker' ? styles.readinessBlocker : styles.readinessWarning}`}>
                  <span className={styles.readinessIcon}>{item.type === 'blocker' ? '\u26D4' : '\u26A0'}</span>
                  <div>
                    {item.employeeName && <strong>{item.employeeName}</strong>}
                    <span>{item.message}</span>
                  </div>
                </div>
              ))}
            </div>
          )}

          {readiness && readiness.items.length === 0 && (
            <div className={styles.readinessOk}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M20 6L9 17l-5-5" /></svg>
              <span>{t('readiness.no_warnings')}</span>
            </div>
          )}

          {/* PDFs */}
          {pdfs.length > 0 && (
            <div className={styles.pdfList}>
              <button className={styles.secondaryBtn} onClick={() => pdfs.forEach(dl)}>{t('run_payroll.download_all')} ({pdfs.length})</button>
              {pdfs.map((p, i) => <button key={i} className={styles.pdfItem} onClick={() => dl(p)}><span>{p.name}</span><span className={styles.pdfSize}>{(p.blob.size / 1024).toFixed(0)} KB</span></button>)}
            </div>
          )}

          {/* Actions */}
          <div className={styles.actionBar}>
            <button className={styles.secondaryBtn} onClick={loadGrid}>{t('run_payroll.new_run')}</button>
            {savedIds.length > 0 && (
              <button className={styles.primaryBtn} onClick={validateAll} disabled={validating}>
                {validating ? t('common.loading') : `${t('run_payroll.validate_all')} (${savedIds.length})`}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
